import { useEffect } from 'react';
import { useRouter } from 'next/router';

import {
  newTracker,
  trackPageView,
  enableActivityTracking,
} from '@snowplow/browser-tracker';
import {
  LinkClickTrackingPlugin,
  enableLinkClickTracking,
} from '@snowplow/browser-plugin-link-click-tracking';
import {
  ButtonClickTrackingPlugin,
  enableButtonClickTracking,
} from '@snowplow/browser-plugin-button-click-tracking';
import {
  FormTrackingPlugin,
  enableFormTracking,
} from '@snowplow/browser-plugin-form-tracking';
import { DebuggerPlugin } from '@snowplow/browser-plugin-debugger';
import { getPlatformMeta } from '../platformMeta';
import { isNative } from '@/domains/shared/utils/native';

let initialised = false;

function initSnowplow() {
  if (typeof window === 'undefined' || initialised) return;

  try {
    const appContext = getPlatformMeta();

    const plugins = [
      LinkClickTrackingPlugin(),
      ButtonClickTrackingPlugin(),
      FormTrackingPlugin(),
      ...(localStorage.getItem('SNOWPLOW_DEBUG_ENABLED') === 'true'
        ? [DebuggerPlugin()]
        : []),
    ];

    newTracker('co', 'https://c.pensionbee.com', {
      appId: `${appContext.app_name}-${appContext.app_region}-${appContext.app_env}`,
      platform: isNative() ? 'mob' : 'web',
      eventMethod: 'post',
      discoverRootDomain: true,
      plugins,
      contexts: {
        webPage: true,
        session: true,
      },
    });
    enableActivityTracking({
      minimumVisitLength: 10,
      heartbeatDelay: 10,
    });
    enableLinkClickTracking({ trackContent: true });
    // Only track buttons carrying an explicit id (our `feature-name_action-name`
    // tracking ids), so we capture intentional CTAs without flooding the
    // pipeline with every incidental button click in the app.
    enableButtonClickTracking({
      filter: (element) =>
        /^[a-z]+(-[a-z]+)*_[a-z]+(-[a-z]+)*$/i.test(element.id),
    });
    enableFormTracking();
    initialised = true;
  } catch (error) {
    initialised = false;
    console.error('Snowplow initialisation failed', error);
  }
}

export function useSnowplow(enabled = true) {
  const { asPath } = useRouter();

  useEffect(() => {
    if (!enabled) return;
    initSnowplow();
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !initialised) return;
    trackPageView();
  }, [asPath, enabled]);
}
